import React, { useState } from 'react';
import { S3Client, ListObjectsV2Command, PutObjectCommand } from "@aws-sdk/client-s3";

const AWSCredentialsTester = () => {
  const [testResults, setTestResults] = useState([]);
  const [testing, setTesting] = useState(false);

  // Update these with your actual credentials
  const AWS_ACCESS_KEY_ID = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
  const AWS_SECRET_ACCESS_KEY = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
  const AWS_REGION = "ap-south-1";
  const S3_BUCKET = "easylearn";

  const addResult = (message, success = true, details = null) => {
    setTestResults(prev => [...prev, {
      message,
      success,
      details,
      time: new Date().toLocaleTimeString()
    }]);
  };

  const runTests = async () => {
    setTesting(true);
    setTestResults([]);

    // Test 1: Check if credentials are loaded from environment
    addResult("Test 1: Checking environment variables...");
    
    if (!AWS_ACCESS_KEY_ID || !AWS_SECRET_ACCESS_KEY) {
      addResult("❌ Environment variables not loaded!", false, {
        hint: "Make sure .env file is in root directory and you've restarted the dev server"
      });
      setTesting(false);
      return;
    }

    addResult("✅ Environment variables loaded", true, {
      accessKeyId: `${AWS_ACCESS_KEY_ID.substring(0, 4)}...${AWS_ACCESS_KEY_ID.substring(AWS_ACCESS_KEY_ID.length - 4)}`,
      region: AWS_REGION,
      bucket: S3_BUCKET
    });

    // Test 2: Create S3 client
    addResult("Test 2: Creating S3 client...");
    
    const s3Client = new S3Client({
      region: AWS_REGION,
      credentials: {
        accessKeyId: AWS_ACCESS_KEY_ID,
        secretAccessKey: AWS_SECRET_ACCESS_KEY,
      },
    });

    addResult("✅ S3 client created", true);

    // Test 3: List objects (verify credentials work)
    addResult("Test 3: Testing bucket access (ListObjects)...");
    
    try {
      const listCommand = new ListObjectsV2Command({
        Bucket: S3_BUCKET,
        MaxKeys: 1
      });
      
      const response = await s3Client.send(listCommand);
      addResult("✅ Successfully accessed bucket!", true, {
        bucket: S3_BUCKET,
        objectCount: response.KeyCount || 0
      });
    } catch (error) {
      addResult("❌ Failed to access bucket", false, {
        error: error.message,
        code: error.Code,
        statusCode: error.$metadata?.httpStatusCode,
        hint: getErrorHint(error.Code)
      });
      setTesting(false);
      return;
    }

    // Test 4: Test upload with small test file
    addResult("Test 4: Testing upload capability...");
    
    try {
      const testContent = new TextEncoder().encode("AWS S3 Upload Test - " + new Date().toISOString());
      const testKey = `test/credential-test-${Date.now()}.txt`;
      
      const putCommand = new PutObjectCommand({
        Bucket: S3_BUCKET,
        Key: testKey,
        Body: testContent,
        ContentType: 'text/plain',
        ACL: 'public-read'
      });
      
      const uploadResponse = await s3Client.send(putCommand);
      const testUrl = `https://${S3_BUCKET}.s3.${AWS_REGION}.amazonaws.com/${testKey}`;
      
      addResult("✅ Upload successful!", true, {
        testUrl: testUrl,
        etag: uploadResponse.ETag
      });
      
      // Test 5: Verify the uploaded file is accessible
      addResult("Test 5: Verifying uploaded file is accessible...");
      
      try {
        const verifyResponse = await fetch(testUrl);
        if (verifyResponse.ok) {
          addResult("✅ File is publicly accessible!", true, {
            url: testUrl,
            status: verifyResponse.status
          });
        } else {
          addResult("⚠️ File uploaded but not publicly accessible", false, {
            status: verifyResponse.status,
            hint: "Check bucket policy and ACL settings"
          });
        }
      } catch (error) {
        addResult("⚠️ Could not verify file accessibility", false, {
          error: error.message
        });
      }
      
    } catch (error) {
      addResult("❌ Upload failed", false, {
        error: error.message,
        code: error.Code,
        hint: getErrorHint(error.Code)
      });
    }

    setTesting(false);
  };

  const getErrorHint = (errorCode) => {
    const hints = {
      'InvalidAccessKeyId': 'The Access Key ID does not exist. Check if you copied it correctly.',
      'SignatureDoesNotMatch': 'The Secret Access Key is incorrect. Check for typos or extra spaces.',
      'AccessDenied': 'Credentials are valid but lack permissions. Check IAM policies.',
      'NoSuchBucket': 'The bucket "easylearn" does not exist or is in a different region.',
      'RequestTimeTooSkewed': 'Your system clock is out of sync. Please sync your system time.',
      'InvalidToken': 'Session token is invalid. Are you using temporary credentials?'
    };
    return hints[errorCode] || 'Unknown error. Check AWS console for more details.';
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>AWS Credentials Tester</h2>
      
      <button 
        onClick={runTests} 
        disabled={testing}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: testing ? '#ccc' : '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: testing ? 'not-allowed' : 'pointer'
        }}
      >
        {testing ? 'Testing...' : 'Run Tests'}
      </button>

      <div style={{ marginTop: '20px' }}>
        {testResults.map((result, index) => (
          <div key={index} style={{
            padding: '10px',
            margin: '10px 0',
            backgroundColor: result.success ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${result.success ? '#4caf50' : '#f44336'}`,
            borderRadius: '4px'
          }}>
            <div style={{ fontWeight: 'bold' }}>
              [{result.time}] {result.message}
            </div>
            {result.details && (
              <pre style={{ 
                fontSize: '12px', 
                marginTop: '5px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}>
                {JSON.stringify(result.details, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AWSCredentialsTester;